// src/api.ts
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

export interface Review {
  id: number;
  address: string;
  from_date: string;
  to_date: string;
  management_rating: number;
  area_rating: number;
  safety_rating: number;
  maintenance_rating: number;
  amenities_rating: number;
  review_text: string;
  latitude: number;
  longitude: number;
  created_at: string;
}

export async function fetchReviews(address: string): Promise<Review[]> {
  const res = await fetch(
    `${API_BASE}/reviews/${encodeURIComponent(address)}`
  );
  if (!res.ok) throw new Error("Failed to fetch reviews");
  const body = await res.json();
  return body.reviews as Review[];
}

export interface NewReviewPayload {
  address: string;
  fromDate: string;
  toDate: string;
  managementRating: number;
  areaRating: number;
  safetyRating: number;
  maintenanceRating: number;
  amenitiesRating: number;
  reviewText?: string;
  latitude: number;
  longitude: number;
}

export interface ReviewLocationSummary {
  address: string;
  latitude: number;
  longitude: number;
  review_count: number;
}

export interface MapBounds {
  south: number;
  north: number;
  west: number;
  east: number;
}

/** Properties that have at least one review within the map rectangle. */
export async function fetchReviewLocationsInBounds(
  bounds: MapBounds,
  options?: { signal?: AbortSignal }
): Promise<ReviewLocationSummary[]> {
  const q = new URLSearchParams({
    south: String(bounds.south),
    north: String(bounds.north),
    west: String(bounds.west),
    east: String(bounds.east),
  });
  const res = await fetch(`${API_BASE}/reviews/bounds?${q.toString()}`, {
    signal: options?.signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to load map locations");
  }
  const body = await res.json();
  return body.locations as ReviewLocationSummary[];
}

export async function postReview(payload: NewReviewPayload) {
  const res = await fetch(`${API_BASE}/reviews`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to submit review");
  }
  return res.json();
}
